<template>
    <v-card-title class="pa-0" flat dense>
        <v-container fluid class="pa-0">
            <v-row no-gutters align="center">
                <v-col cols="auto" class="py-1">
                    <v-btn
                        color="secondary"
                        class="mx-2"
                        outlined
                        @click="showJobParametersDialog"
                    >
                        Parametry zlecenia
                    </v-btn>

                    <v-btn
                        color="secondary"
                        class="mx-2"
                        outlined
                        :disabled="!canEditJob"
                        @click="showImpositionOrdinalNumbersDialog"
                    >
                        Numeracja montaży
                    </v-btn>
                </v-col>

                <v-divider vertical />

                <v-col cols="auto">
                    <v-btn-toggle
                        v-model="viewType"
                        color="secondary"
                        dense
                        tile
                        group
                        @change="changeDiagramView"
                    >
                        <v-tooltip :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn value="job" data-sentry-label="Widok pełnego zlecenia" class="my-0" v-on="on">
                                    <v-icon>
                                        mdi-graph
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Widok pełnego zlecenia</span>
                        </v-tooltip>
                        <v-tooltip :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn value="imposition" data-sentry-label="Widok montaży" class="my-0" v-on="on">
                                    <v-icon>
                                        mdi-source-fork
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Widok montaży</span>
                        </v-tooltip>
                    </v-btn-toggle>
                </v-col>

                <v-divider vertical />

                <v-col cols="auto" md="3" lg="2" class="py-1">
                    <v-select
                        v-model="impositionIndex"
                        :items="impositionNames"
                        dense
                        item-value="index"
                        item-text="text"
                        hide-details
                        outlined
                        class="mx-2"
                        data-sentry-label="Montaż"
                        :disabled="viewType === 'job'"
                        @change="changeImposition"
                    />
                </v-col>

                <v-divider vertical />

                <v-col cols="auto" class="py-1">
                    <v-btn
                        v-if="canEditJob"
                        color="primary"
                        class="mx-2"
                        outlined
                        @click="showManageComponentsDialog"
                    >
                        <template>
                            Edycja zbiorcza
                        </template>
                    </v-btn>
                    <v-btn
                        color="secondary"
                        class="mx-2"
                        outlined
                        :disabled="!isTaskSelected"
                        @click="showTaskParametersDialog"
                    >
                        <template v-if="canEditJob">
                            Edycja procesu
                        </template>
                        <template v-else>
                            Podgląd procesu
                        </template>
                    </v-btn>

                    <v-tooltip :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                :disabled="!isTaskSelected || !canEditJob"
                                icon
                                text
                                small
                                class="mx-2"
                                v-on="on"
                                @click="showConfirmDeleteTaskDialog"
                            >
                                <v-icon color="pink">
                                    mdi-delete
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Usuń proces</span>
                    </v-tooltip>

                    <v-tooltip v-if="isJobInProgress || isJobClosed" :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                text
                                small
                                class="mr-2"
                                data-sentry-label="Generuj raport"
                                v-on="on"
                                @click="showPrintReportsDialog"
                            >
                                <v-icon color="blue">
                                    mdi-printer
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Generuj raport</span>
                    </v-tooltip>

                    <v-menu v-if="!isJobCancelled" offset-y>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                class="mx-2"
                                v-on="on"
                            >
                                <v-icon color="secondary">
                                    mdi-cog
                                </v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                v-if="isTraderUser && !isJobClosed && !isJobCancelled"
                                data-sentry-label=" Przekaż do technologa"
                                :disabled="isJobInProgress"
                                @click="showConfirmForwardJobDialog"
                            >
                                <v-list-item-icon>
                                    <v-icon>
                                        mdi-file-move
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title> Przekaż do technologa</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="(isTechnologistUser || userWithAutoCloseSetup) && isJobInProgress"
                                data-sentry-label="Zamknij zlecenie"
                                @click="showConfirmCloseJobDialog"
                            >
                                <v-list-item-icon>
                                    <v-icon>
                                        mdi-file-check
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title> Zamknij zlecenie</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="isTechnologistUser && isJobClosed && !isJobCancelled"
                                data-sentry-label="Otwórz zlecenie"
                                @click="showConfirmReopenJobDialog"
                            >
                                <v-list-item-icon>
                                    <v-icon>
                                        mdi-file-refresh
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title> Otwórz zlecenie</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="isTechnologistUser && isJobClosed && !isJobCancelled"
                                data-sentry-label="Dodruk zlecenia"
                                :disabled="isJobReprinted"
                                @click="showReprintJobDialog"
                            >
                                <v-list-item-icon>
                                    <v-icon>
                                        mdi-file-plus
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title> Dodruk zlecenia</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="hasJobsDeletePermission"
                                data-sentry-label="Wyślij zapotrzebowanie"
                                :disabled="isSendRequestDisabled"
                                @click="showConfirmSendRequestDialog"
                            >
                                <v-list-item-icon>
                                    <v-icon>
                                        mdi-email-send
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title> Wyślij zapotrzebowanie</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                data-sentry-label="Anuluj zlecenie"
                                @click="showConfirmCancelJobDialog"
                            >
                                <v-list-item-icon>
                                    <v-icon>
                                        mdi-file-cancel
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title> Anuluj zlecenie</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-col>

                <v-divider vertical />

                <v-col cols="auto" class="py-1">
                    <v-tooltip v-if="hasJobChangeHistoryPermission" :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                text
                                small
                                class="mx-2"
                                data-sentry-label="Historia zmian"
                                v-on="on"
                                @click="showJobChangeHistoryDialog"
                            >
                                <v-icon color="secondary">
                                    mdi-history
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Historia zmian</span>
                    </v-tooltip>

                    <v-tooltip :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                small
                                text
                                class="mx-1"
                                data-sentry-label="Dopasuj"
                                v-on="on"
                                @click="zoomToFit"
                            >
                                <v-icon>
                                    mdi-fit-to-page-outline
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Dopasuj</span>
                    </v-tooltip>

                    <v-tooltip :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                small
                                text
                                class="mx-1"
                                data-sentry-label="Sortuj"
                                v-on="on"
                                @click="sort"
                            >
                                <v-icon>
                                    mdi-sort
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Sortuj</span>
                    </v-tooltip>
                </v-col>    
                <v-btn
                    v-if="showAdviceNoteButton"
                    color="secondary"
                    class="mx-2 mb-1"
                    data-sentry-label="Awizacja od klienta"
                    @click="showAdviceNoteDialog"
                >
                    Awizacja od klienta
                </v-btn>
            </v-row>
        </v-container>
    </v-card-title>
</template>

<script>
import { appConfig } from '@/config'

import tooltipDelay from '@/mixins/tooltip'

import { EventBus } from '@/utils/eventBus'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'DiagramToolbar',
    mixins: [
        tooltipDelay
    ],
    props: {
        impositions: {
            type: Array,
            default: null
        },
        task: {
            type: Object,
            default: null
        },
        jobStatus: {
            type: Object,
            default: null
        },
        isJobReprinted: {
            type: Boolean
        },
        actions: {
            type: Object,
            default: null
        },
        jobId: {
            type: Number,
            default: null
        },
        adviceNote: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            impositionIndex: 0,
            viewType: 'job'
        }
    },
    computed: {
        impositionNames() {
            if (!this.impositions) return

            return this.impositions.map((item, index) => ({
                text: `Montaż ${index + 1} z ${this.impositions.length}`,
                index: index
            }))
        },
        isTaskSelected() {
            return Object.entries(this.task).length !== 0
        },
        isPrintTask() {
            return this.task.processId === appConfig.common.processes.printTaskId
        },
        isTraderUser() {
            return hasUserPermission('jobs_trader_update')
        },
        isTechnologistUser() {
            return hasUserPermission('jobs_update')
        },
        userWithAutoCloseSetup() {
            return hasUserPermission('jobs_trader_update_close_job')
        },
        hasJobsDeletePermission() {
            return hasUserPermission('jobs_delete')
        },
        hasAdviceNotePermission() {
            return hasUserPermission('advice_notes_update')
        },
        hasJobChangeHistoryPermission() {
            return hasUserPermission(['jobs_read', 'jobs_trader_read'])
        },
        showAdviceNoteButton() {
            return this.hasAdviceNotePermission && this.adviceNote && this.adviceNote.id
        },
        canEditJob() {
            return (this.isTraderUser && (!this.isJobInProgress && !this.isJobClosed && !this.isJobCancelled)) ||
            ((this.isTechnologistUser || this.userWithAutoCloseSetup) && !this.isJobClosed && !this.isJobCancelled)
        },
        isJobInProgress() {
            return this.jobStatus.id === 20
        },
        isJobClosed() {
            return this.jobStatus.id === 40
        },
        isJobCancelled() {
            return this.jobStatus.id === 50
        },
        isJobDraft() {
            return this.jobStatus.id === 10
        },
        isSendRequestDisabled() {
            return this.isJobDraft || this.isJobClosed || this.isJobCancelled
        }
    },
    destroyed() {
        EventBus.$off()
    },
    methods: {
        changeDiagramView() {
            EventBus.$emit('changeDiagramView', this.viewType)
        },
        changeImposition() {
            EventBus.$emit('changeImposition', this.impositionIndex)
        },
        showJobParametersDialog() {
            EventBus.$emit('showJobParametersDialog')
        },
        showImpositionOrdinalNumbersDialog() {
            EventBus.$emit('showImpositionOrdinalNumbersDialog')
        },
        showTaskParametersDialog() {
            this.isPrintTask ? EventBus.$emit('showImpositionParametersDialog') : EventBus.$emit('showTaskParametersDialog')
        },
        showConfirmDeleteTaskDialog() {
            EventBus.$emit('showConfirmDeleteTaskDialog')
        },
        showConfirmForwardJobDialog() {
            EventBus.$emit('showConfirmForwardJobDialog')
        },
        showConfirmCloseJobDialog() {
            EventBus.$emit('showConfirmCloseJobDialog')
        },
        showReprintJobDialog() {
            EventBus.$emit('showReprintJobDialog')
        },
        showConfirmReopenJobDialog() {
            EventBus.$emit('showConfirmReopenJobDialog')
        },
        showConfirmCancelJobDialog() {
            EventBus.$emit('showConfirmCancelJobDialog')
        },
        showConfirmSendRequestDialog() {
            EventBus.$emit('showConfirmSendRequestDialog')
        },
        showPrintReportsDialog() {
            EventBus.$emit('showPrintReportsDialog')
        },
        zoomToFit() {
            EventBus.$emit('diagramZoomToFit')
        },
        reset() {
            EventBus.$emit('diagramReset')
        },
        undo() {
            EventBus.$emit('diagramUndo')
        },
        redo() {
            EventBus.$emit('diagramRedo')
        },
        sort() {
            EventBus.$emit('diagramSort')
        },
        showManageComponentsDialog() {
            EventBus.$emit('showManageComponentsDialog')
        },
        showAdviceNoteDialog() {
            EventBus.$emit('showAdviceNoteDialog')
        },
        showJobChangeHistoryDialog() {
            EventBus.$emit('showJobChangeHistoryDialog')
        }
    }
}
</script>
